import React, { useEffect, useMemo, useState } from 'react';
import CompanyTabBar from '../workflow/CompanyTabBar';
import EngagementLeadsBox from '../workflow/EngagementLeadsBox';
import { engagementLabelsOf } from '../workflow/CoverageDisplay';
import { engagementDisplayName } from '../../utils/engagementName';
import RefLink from '../shared/RefLink';
import { FormModal, Field, inputCls, btnPrimary, btnGhost } from '../shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../shared/formValidation';
import GroupedEngagementPicker from '../workflow/GroupedEngagementPicker';
import EditableAttachments from '../workflow/EditableAttachments';
import FileList from '../shared/FileList';
import { recordKind } from './recordKinds';
import { useLegacyImportSetting } from './legacyImport';
import { fetchJSON, API_BASE_URL } from '../../utils/api';

// 正式記錄的**建立與編輯共用的一支表單**。
//
// `record` 有值＝編輯、沒有＝建立。這是 QC6/QC7 記錄頁已經驗證過的形狀
// （RecordFormModal 被清單頁與詳情頁共用），差別只在這裡一支要吃多種 kind。
// CW/CC 不在管理面（見 recordKinds.js）。涵蓋區塊看 kind 的 `coverage`：
// Signed Financial Statements 與 QC35 都是單案（一間公司一筆；legacy 多案記錄開進編輯時
// 既有的多個涵蓋照列，可逐一移除、不可再加）。
//
// 外殼負責所有 kind 都一樣的部分：涵蓋挑選、既有附件的呈現與刪除、回寫說明、
// 送出與錯誤；kind 專屬的欄位（含簽署文件的上傳欄——fs 掛簽署日自動偵測、
// qc35 只有 file 方法才有）由 recordKinds 的 `form.Fields` 提供，外殼只把
// files/setFiles 交給它。三份表單各自漂移的下場已經在 KINDS 被抄成兩份的時候
// 看過了。
//
// 附件的兩種來源：workflow 核准複製過來的列與申請單**共用 binary**，
// 刪不得（後端 409）；Management 自己上傳的才給 Remove。所以只把 manualUpload
// 的列餵給 EditableAttachments，其餘另外列成唯讀。

// 一次建多筆（kind 設定 `batchCreate`，fs 與 qc35）：建立時可勾同集團、FYE 同年的
// 多家公司，一家一個分頁、一家一筆記錄（與 workflow 的 Signed Financial
// Statements New 頁同一種分頁）。分頁全部掛著（只藏非作用中的），各自的上傳與
// 簽署日偵測切來切去不會掉；Remarks 共用。送出逐家 POST，失敗的那幾家留在頁上
// 並寫明原因，成功的拿掉。編輯維持單筆。
const EngagementRecordFormModal = ({ kind, record, onClose, onSaved }) => {
  const cfg = recordKind(kind);
  if (cfg.form.batchCreate && !record) {
    return <BatchCreateModal kind={kind} cfg={cfg} onClose={onClose} onSaved={onSaved} />;
  }
  return <SingleRecordModal kind={kind} cfg={cfg} record={record} onClose={onClose} onSaved={onSaved} />;
};

const BatchCreateModal = ({ kind, cfg, onClose, onSaved }) => {
  const [options, setOptions] = useState([]);
  const [optionsError, setOptionsError] = useState(null);
  const [ids, setIds] = useState([]);
  const [forms, setForms] = useState({});   // { [engagementId]: cfg.form.initial(null) }
  const [files, setFiles] = useState({});   // { [engagementId]: File[] }
  const [activeId, setActiveId] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJSON(`/api/qm/engagement-records/${kind}/options`);
        setOptions(data.engagements || []);
      } catch (e) { setOptionsError(e.message); }
    })();
  }, [kind]);

  const engOf = (id) => options.find((e) => e.id === id) || null;
  // 公司名；同公司多案時加服務／描述（頁籤第二行、句子裡的括號）。
  const labels = engagementLabelsOf(ids.map(engOf));
  const nameOf = (id) => labels[id]?.text || `#${id}`;
  const multi = ids.length > 1;

  const updateSelection = (next) => {
    setError(null);
    setIds(next);
    setForms((prev) => Object.fromEntries(next.map((id) => [id, prev[id] || { ...cfg.form.initial(null), engagementIds: [id] }])));
    setFiles((prev) => Object.fromEntries(next.map((id) => [id, prev[id] || []])));
    setActiveId((cur) => (next.includes(cur) ? cur : (next[0] || null)));
  };
  const setFieldOf = (id) => (key, value) => setForms((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));

  // 已經有記錄的案子：後端一案一筆會 409，選了就先講。
  const existing = ids.map(engOf).filter((e) => e && e.existingRecord);

  const { missingSignedFormAllowed } = useLegacyImportSetting();
  const companyGaps = (id) => cfg.form.gaps(forms[id] || cfg.form.initial(null), { allowMissingSignedForm: missingSignedFormAllowed })
    .filter((g) => g.key !== 'engagementIds');
  const gaps = useMemo(() => [
    ...(ids.length === 0 ? [{ key: 'engagementIds', label: 'Engagement' }] : []),
    ...ids.flatMap((id) => companyGaps(id).map((g) => ({
      key: `${id}:${g.key}`,
      label: multi ? `${nameOf(id)} — ${g.label}` : g.label,
    }))),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [ids, forms, options, multi, missingSignedFormAllowed]);
  const validation = useFormValidation(gaps);
  // 每個分頁只標自己那家的缺漏（拿掉前綴，欄位的 field= 照舊）。
  const panelValidation = (id) => {
    const byKey = {};
    const prefix = `${id}:`;
    for (const [k, g] of Object.entries(validation.providerValue.byKey)) {
      if (k.startsWith(prefix)) byKey[k.slice(prefix.length)] = g;
    }
    return { byKey };
  };
  const tabTag = (id) => {
    if (engOf(id)?.existingRecord) return { text: 'Has Record', tone: 'todo' };
    const missing = companyGaps(id).length;
    if (missing === 0) return { text: 'Ready', tone: 'done' };
    return validation.attempted ? { text: `${missing} Missing`, tone: 'todo' } : { text: 'Incomplete', tone: 'muted' };
  };
  const busy = ids.some((id) => forms[id]?.detectBusy);
  // 部分已存：關掉時回清單（那幾筆已經建好了）。
  const close = () => (savedCount > 0 ? onSaved(null, null) : onClose());

  const save = async () => {
    if (!validation.validate()) {
      setError(null);
      const first = ids.find((id) => companyGaps(id).length > 0);
      if (first) setActiveId(first);
      return;
    }
    for (const id of ids) {
      const problems = cfg.form.problems(forms[id], { selected: [engOf(id)].filter(Boolean) });
      if (problems.length > 0) { setActiveId(id); setError(`${nameOf(id)}: ${problems[0]}`); return; }
    }
    setSaving(true);
    setError(null);
    const saved = [];
    const failed = [];
    for (const id of ids) {
      try {
        const fd = new FormData();
        cfg.form.toBody(forms[id], fd);
        fd.set('remarks', remarks || '');
        (files[id] || []).forEach((f) => fd.append('files', f));
        // eslint-disable-next-line no-await-in-loop
        const data = await fetchJSON(`/api/qm/engagement-records/${kind}`, { method: 'POST', body: fd });
        saved.push({ id, record: data.record, applied: data.applied });
      } catch (e) { failed.push({ id, error: e.message }); }
    }
    setSaving(false);
    if (failed.length === 0) {
      // 一筆進那筆記錄；多筆回清單。
      if (saved.length === 1 && savedCount === 0) onSaved(saved[0].record, saved[0].applied);
      else onSaved(null, null);
      return;
    }
    setSavedCount((n) => n + saved.length);
    updateSelection(failed.map((f) => f.id));
    setError(`${saved.length > 0 ? `${saved.length} record(s) saved. ` : ''}Not saved: ${failed.map((f) => `${nameOf(f.id)} (${f.error})`).join('; ')}.`);
  };

  return (
    <FormModal
      title={`New ${cfg.form.title}`}
      size="xl"
      onClose={close}
      footer={(
        <>
          <button type="button" onClick={close} className={btnGhost}>Cancel</button>
          <button type="button" onClick={save} disabled={saving || busy} className={btnPrimary}>
            {saving ? 'Saving…' : multi ? `Save ${ids.length} Records` : 'Save'}
          </button>
        </>
      )}
    >
      <FormValidationProvider value={validation.providerValue}>
        {(error || validation.error) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error || validation.error}</div>
        )}
        {optionsError && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            Could not load the engagement list: {optionsError}
          </div>
        )}

        <Field
          label="Engagement(s)"
          required
          field="engagementIds"
          hint="One record per company — select several companies of the same client group and FYE year to create their records together, each on its own tab."
        >
          <GroupedEngagementPicker engagements={options} selectedIds={ids} onChange={updateSelection} />
        </Field>

        {existing.length > 0 && (
          <div className="border border-amber-200 rounded-lg p-3 bg-amber-50 text-sm text-amber-800">
            <p className="font-medium mb-1">These engagements already have a record — corrections go through the amendment flow:</p>
            {existing.map((e) => (
              <p key={e.id}>
                {nameOf(e.id)}{' — '}
                <RefLink to={`${cfg.listTo}/${e.existingRecord.id}`} className="font-medium underline hover:text-amber-900">
                  {e.existingRecord.sourceRequestNo || `#${e.existingRecord.id}`}
                </RefLink>
              </p>
            ))}
          </div>
        )}

        {multi && (
          <CompanyTabBar
            items={ids.map((id) => ({ id, label: labels[id]?.label || `#${id}`, sub: labels[id]?.sub, title: engagementDisplayName(engOf(id)?.engagementName, engOf(id)?.serviceDescription), tag: tabTag(id) }))}
            activeId={activeId}
            onSelect={setActiveId}
          />
        )}
        {ids.map((id) => (forms[id] ? (
          <div key={id} role="tabpanel" hidden={id !== activeId} className={id !== activeId ? 'hidden' : 'space-y-4'}>
            <FormValidationProvider value={panelValidation(id)}>
              {engOf(id) && <EngagementLeadsBox engagement={engOf(id)} showSignOff={cfg.form.showSignOff} />}
              <cfg.form.Fields
                form={forms[id]}
                setField={setFieldOf(id)}
                options={options}
                files={files[id] || []}
                setFiles={(list) => setFiles((prev) => ({ ...prev, [id]: list }))}
                isEdit={false}
                record={null}
                copy={multi && cfg.form.copyFrom ? {
                  sources: ids.filter((x) => x !== id).map((x) => ({ id: x, label: nameOf(x) })),
                  onCopy: (srcId) => setForms((prev) => ({ ...prev, [id]: cfg.form.copyFrom(prev[srcId], prev[id]) })),
                } : null}
              />
            </FormValidationProvider>
          </div>
        ) : null))}

        <Field label="Remarks">
          <textarea rows="2" value={remarks} onChange={(e) => setRemarks(e.target.value)} className={inputCls} />
        </Field>

      </FormValidationProvider>
    </FormModal>
  );
};

const SingleRecordModal = ({ kind, cfg, record, onClose, onSaved }) => {
  const isEdit = !!record;

  const [options, setOptions] = useState([]);
  const [optionsError, setOptionsError] = useState(null);
  const [form, setForm] = useState(() => cfg.form.initial(record));
  const [files, setFiles] = useState([]);
  const [removeIds, setRemoveIds] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchJSON(`/api/qm/engagement-records/${kind}/options`);
        setOptions(data.engagements || []);
      } catch (e) { setOptionsError(e.message); }
    })();
  }, [kind]);

  const selected = useMemo(
    () => options.filter((e) => form.engagementIds.includes(e.id)),
    [options, form.engagementIds]
  );

  // 已經有記錄涵蓋這些案子——**提示不擋**（重簽與 restatement 確實會有第二份
  // 文件）。CW/CC 例外：一案一筆，後端直接 409，所以那邊的提示等同警告。
  //
  // 兩層過濾都是必要的：**正在編輯的這一筆**當然涵蓋這些案子，報出來只會讓人
  // 以為撞單；而一筆記錄涵蓋多案時每案都會指回同一筆，不去重就會列出重複項。
  const existing = useMemo(() => {
    const seen = new Map();
    for (const e of selected) {
      const x = e.existingRecord;
      if (x && x.id !== record?.id) seen.set(x.id, x);
    }
    return [...seen.values()];
  }, [selected, record]);

  // 空白的必填格：按 Save 後就地標紅並列進橫幅，填好就消。
  const { missingSignedFormAllowed } = useLegacyImportSetting();
  const gaps = useMemo(
    () => cfg.form.gaps(form, { record, allowMissingSignedForm: missingSignedFormAllowed }),
    [cfg, form, record, missingSignedFormAllowed]
  );
  const validation = useFormValidation(gaps);

  const save = async () => {
    if (!validation.validate()) { setError(null); return; }
    // 填了但不合規（連結格式、歸檔日早於簽署日）——規則錯誤走 modal 自己的
    // 錯誤列；`selected` 是涵蓋案的完整列（帶 signOffDate），日期規則要它。
    const problems = cfg.form.problems(form, { selected });
    if (problems.length > 0) { setError(problems[0]); return; }
    setSaving(true);
    setError(null);
    try {
      const fd = new FormData();
      cfg.form.toBody(form, fd);
      fd.set('remarks', form.remarks || '');
      if (isEdit && removeIds.length > 0) fd.set('removeFileIds', JSON.stringify(removeIds));
      files.forEach((f) => fd.append('files', f));

      const path = isEdit
        ? `/api/qm/engagement-records/${kind}/${record.id}`
        : `/api/qm/engagement-records/${kind}`;
      const data = await fetchJSON(path, { method: isEdit ? 'PATCH' : 'POST', body: fd });
      onSaved(data.record, data.applied);
    } catch (e) { setError(e.message); setSaving(false); }
  };

  // 這筆記錄現在涵蓋、但表單上選不到的案子。存檔會把涵蓋整組換成表單上的選
  // 擇，所以這些會掉——一定要講出來。兩種成因，同一個後果：
  //   案子被刪了（涵蓋表是 ON DELETE SET NULL，只留案名快照）
  //   挑選器只展開「同一 Registration 的兄弟公司」，跨註冊來源的第二案選不進去
  const dropped = (record?.engagements || []).filter(
    (e) => !e.engagementId || !form.engagementIds.includes(e.engagementId)
  );

  // 匯入記錄的現行簽署 Form QC35 只能換版（欄位在上面），不給移除鈕——
  // 後端也擋（409 SIGNED_FORM_LOCKED）。
  const ownFiles = (record?.files || []).filter((f) => f.manualUpload && !f.supersededAt
    && !(kind === 'qc35' && record?.legacyImport && f.docType === 'qc35'));
  const requestFiles = (record?.files || []).filter((f) => !f.manualUpload && !f.supersededAt);

  return (
    <FormModal
      title={`${isEdit ? 'Edit' : 'New'} ${cfg.form.title}`}
      size="xl"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          {/* detectBusy＝簽署日 PDF 掃描中（fs 專屬）——掃到一半存檔會帶舊日期 */}
          <button type="button" onClick={save} disabled={saving || form.detectBusy} className={btnPrimary}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    >
      <FormValidationProvider value={validation.providerValue}>
      {(error || validation.error) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error || validation.error}</div>
      )}
      {optionsError && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          Could not load the engagement list: {optionsError}
        </div>
      )}

      {/* ── 涵蓋 ─────────────────────────────────────────────────────────── */}
      <Field
        label={cfg.coverage === 'single' ? 'Engagement' : 'Engagement(s)'}
        required
        field="engagementIds"
        hint={cfg.coverage === 'single'
          ? 'One record covers one company — the selected engagement is written back.'
          : 'One record may cover several companies of the same registration. Every ticked engagement is written back.'}
      >
        <GroupedEngagementPicker
          engagements={options}
          selectedIds={form.engagementIds}
          onChange={(ids) => setField('engagementIds', ids)}
          single={cfg.coverage === 'single'}
        />
      </Field>

      {dropped.length > 0 && (
        <div className="border border-amber-200 rounded-lg p-3 bg-amber-50 text-sm text-amber-800">
          <p className="font-medium mb-1">
            Saving drops {dropped.length} engagement(s) this record currently covers — they are not in
            the selection above. Their names stay readable in this record&rsquo;s history, but the record
            will no longer cover them.
          </p>
          {dropped.map((e) => (
            <p key={e.engagementId || e.engagementName}>
              {engagementDisplayName(e.engagementName, e.serviceDescription)}
              {!e.engagementId && <span className="text-amber-600"> · engagement no longer exists</span>}
            </p>
          ))}
        </div>
      )}

      {existing.length > 0 && (
        <div className="border border-amber-200 rounded-lg p-3 bg-amber-50 text-sm text-amber-800">
          <p className="font-medium mb-1">
            Some of the selected engagements are already covered by a record:
          </p>
          {existing.map((x) => (
            <p key={x.id}>
              <RefLink to={`${cfg.listTo}/${x.id}`} className="font-medium underline hover:text-amber-900">
                {x.sourceRequestNo || `#${x.id}`}
              </RefLink>
              {x.count > 1 ? ` — ${x.count} records` : ''}
            </p>
          ))}
        </div>
      )}

      {/* ── kind 專屬欄位（含簽署文件上傳——與 workflow 表單同觀感） ──
          files 交給 kind 欄位自己呈現：fs 要掛簽署日自動偵測、qc35 只有 file
          方法才有上傳，外殼不知道這些分支。編輯時上傳＝換版（後端 supersede，
          舊版標 previous version 留在記錄上）。 */}
      <cfg.form.Fields
        form={form}
        setField={setField}
        options={options}
        files={files}
        setFiles={setFiles}
        isEdit={isEdit}
        record={record}
      />

      <Field label="Remarks">
        <textarea
          rows="2"
          value={form.remarks}
          onChange={(e) => setField('remarks', e.target.value)}
          className={inputCls}
        />
      </Field>

      {/* ── 附件 ─────────────────────────────────────────────────────────── */}
      {isEdit && requestFiles.length > 0 && (
        <Field
          label="Documents from the Approved Request"
          hint="Shared with the request that produced this record — they cannot be removed here."
        >
          <FileList
            files={requestFiles}
            divided={false}
            className="space-y-1"
            row={(f) => ({
              name: f.originalName,
              href: `${API_BASE_URL}/api/qm/engagement-records/${kind}/${record.id}/files/${f.id}`,
              pad: 'py-1.5',
            })}
          />
        </Field>
      )}

      {isEdit && ownFiles.length > 0 && (
        <EditableAttachments
          files={ownFiles}
          removeIds={removeIds}
          onToggle={(id) => setRemoveIds((prev) => (
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
          ))}
          downloadHref={(f) => `${API_BASE_URL}/api/qm/engagement-records/${kind}/${record.id}/files/${f.id}`}
          label="Documents Added Here"
          hint="Uploaded on this page — removing one deletes it."
        />
      )}

      {/* 回寫是這頁最容易被誤解的地方——說在按鈕上面，不是存完才發現。 */}
      </FormValidationProvider>
    </FormModal>
  );
};

export default EngagementRecordFormModal;
